import React from 'react';
import clsx from 'clsx';
import SwiperType from 'swiper';

interface NavigationDotsProps {
  swiper: SwiperType | null;
  currentSlide: number;
  totalSlides: number;
  onDotClick: (index: number) => void;
  maxVisibleDots?: number;
  className?: string;
  isAutoPlaying: boolean;
  autoplayDelayMs: number;
  activeColor?: string;
  isClickable?: boolean;
}

const clamp = (n: number, min = 0, max = 100) => Math.min(max, Math.max(min, n));

const NavigationDots: React.FC<NavigationDotsProps> = ({
  swiper,
  currentSlide,
  totalSlides,
  onDotClick,
  maxVisibleDots = 5,
  className = '',
  isAutoPlaying,
  autoplayDelayMs,
  activeColor = '#fff',
  isClickable = true,
}) => {
  const [progress, setProgress] = React.useState(0);
  const [activeMode, setActiveMode] = React.useState<'dot' | 'pill'>(isAutoPlaying ? 'pill' : 'dot');

  const rafRef = React.useRef<number | null>(null);
  const startRef = React.useRef<number>(0);
  const timeoutRef = React.useRef<number | null>(null);

  const stop = React.useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const clearTimers = React.useCallback(() => {
    if (timeoutRef.current) window?.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const startProgress = React.useCallback(() => {
    stop();
    startRef.current = 0;
    setProgress(0);

    if (!isAutoPlaying) return;

    const delay = Math.max(100, autoplayDelayMs || 0);

    const tick = (ts: number) => {
      if (!isAutoPlaying) return;
      if (!startRef.current) startRef.current = ts;

      const elapsed = ts - startRef.current;
      const next = clamp((elapsed / delay) * 100);
      setProgress(next);

      if (next < 100) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [autoplayDelayMs, isAutoPlaying, stop]);

  // Keep mode in sync with pause/play
  React.useEffect(() => {
    clearTimers();
    stop();

    if (!isAutoPlaying) {
      // paused => always dot
      setActiveMode('dot');
      return;
    }

    // playing => pill + progress
    setActiveMode('pill');
    startProgress();

    return () => {
      clearTimers();
      stop();
    };
  }, [isAutoPlaying, startProgress, stop, clearTimers]);

  React.useEffect(() => {
    if (!swiper) return;

    const onSlideChange = () => {
      stop();
      clearTimers();
      setProgress(0);
      setActiveMode('dot');

      requestAnimationFrame(() => {
        if (!isAutoPlaying) return;

        setActiveMode('pill');
        startProgress();
      });
    };

    swiper.on('slideChange', onSlideChange);
    return () => {
      swiper.off('slideChange', onSlideChange);
    };
  }, [swiper, isAutoPlaying, startProgress, stop, clearTimers]);

  if (totalSlides <= 1) return null;

  // Calculate which dots to show
  const getVisibleDots = () => {
    const visible = Math.min(totalSlides, maxVisibleDots);

    if (totalSlides <= visible) return { startIndex: 0, endIndex: totalSlides - 1 };

    const leftCount = Math.floor((visible - 1) / 2);
    const rightCount = visible - 1 - leftCount;

    let startIndex = currentSlide - leftCount;
    let endIndex = currentSlide + rightCount;

    // Shift right if left underflows
    if (startIndex < 0) {
      const delta = -startIndex;
      startIndex = 0;
      endIndex = Math.min(totalSlides - 1, endIndex + delta);
    }

    if (endIndex > totalSlides - 1) {
      const delta = endIndex - (totalSlides - 1);
      endIndex = totalSlides - 1;
      startIndex = Math.max(0, startIndex - delta);
    }

    return { startIndex, endIndex };
  };

  const { startIndex, endIndex } = getVisibleDots();

  const handleDotClick = (index: number) => {
    swiper?.slideTo(index);
    onDotClick(index);
  };

  return (
    <div className={`navigation-dots ${className}`} role="tablist" aria-label="Carousel slide navigation">
      <div className="navigation-dots__main">
        {Array.from({ length: endIndex - startIndex + 1 }, (_, i) => {
          const slideIndex = startIndex + i;
          const isActive = slideIndex === currentSlide;
          const showPill = isActive && isAutoPlaying && activeMode === 'pill';
          const bg = isActive && !isAutoPlaying ? activeColor : '#7A7A7A';

          const dotClass = clsx(
            'relative inline-block h-8 overflow-hidden rounded-full',
            'shadow-md',
            'transition-[width,background-color] duration-300 ease-in-out',
            'will-change-[width]',
            isActive ? 'border-[1.5px] border-[#333333]' : '',
          );
          const dotWidthClass = clsx(showPill ? 'w-20' : 'w-8');

          return (
            <button
              key={slideIndex}
              className={clsx('navigation-dots__button', isClickable ? '!cursor-pointer' : '!cursor-default')}
              disabled={!isClickable}
              tabIndex={!isClickable ? -1 : 0}
              onClick={() => handleDotClick(slideIndex)}
              aria-label={`Go to slide ${slideIndex + 1} of ${totalSlides}`}
              aria-current={isActive ? 'true' : undefined}
              aria-selected={isActive}
              role="tab"
              type="button"
              data-ffid={`btn-nav-hero-banner-${slideIndex}`}
            >
              <span
                className={clsx(dotClass, dotWidthClass)}
                style={{ backgroundColor: bg }}
                aria-hidden="true"
                data-ffid={`ctr-nav-hero-banner-dot-${slideIndex}`}
              >
                {/* Fill only when active is in pill mode */}
                {showPill && (
                  <span
                    className="absolute left-1 top-0 h-6 rounded-full p-1"
                    data-ffid={`prg-nav-hero-banner-dot-progress-${slideIndex}`}
                    style={{
                      width: `${progress}%`,
                      backgroundColor: activeColor,
                    }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NavigationDots;
